import React, { useState } from 'react';
import { X, Eye, EyeOff, Mail } from 'lucide-react';
import { LOGO_URL } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import { forgotPassword } from '../../services/api';

export const LoginModal = ({ isOpen, onClose, onSwitchToRegister }) => {
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) { setError('Please fill all fields'); return; }
    const result = await login(form.email, form.password);
    if (result.success) { 
      onClose(); 
      setForm({ email: '', password: '' }); 
    } else {
      setError(result.error);
    }
  };

  if (showForgotPassword) {
    return <ForgotPasswordModal isOpen={true} onClose={() => { setShowForgotPassword(false); }} onBackToLogin={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-2xl w-full max-w-md p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X size={20} /></button>
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Logo" className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-yellow-400" />
          <h2 className="text-white text-xl font-bold">Login</h2>
          <p className="text-gray-500 text-sm">Welcome back to MARSUPILAMI COIN</p>
        </div>
        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="text-gray-400 text-xs mb-1 block">Email or Username</label>
            <input type="text" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none" placeholder="Enter your email or username" />
          </div>
          <div className="mb-2">
            <label className="text-gray-400 text-xs mb-1 block">Password</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none pr-10" placeholder="Enter your password" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">{showPw ? <EyeOff size={16}/> : <Eye size={16}/>}</button>
            </div>
          </div>
          <div className="mb-6 text-right">
            <button type="button" onClick={() => setShowForgotPassword(true)} className="text-yellow-400 hover:text-yellow-300 text-xs">Forgot Password?</button>
          </div>
          <button type="submit" className="w-full py-3 rounded-xl font-bold text-black text-sm uppercase bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all duration-300">LOGIN</button>
        </form>
        <p className="text-center text-gray-500 text-sm mt-4">Don't have an account? <button onClick={onSwitchToRegister} className="text-yellow-400 hover:text-yellow-300 font-medium">Register</button></p>
      </div>
    </div>
  );
};

export const RegisterModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { register } = useAuth();
  const [form, setForm] = useState({ fullName: '', username: '', email: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fullName || !form.username || !form.email || !form.password) { setError('Please fill all fields'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    const result = await register(form);
    if (result.success) { 
      onClose(); 
      setForm({ fullName: '', username: '', email: '', password: '', confirmPassword: '' }); 
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-2xl w-full max-w-md p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X size={20} /></button>
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Logo" className="w-16 h-16 rounded-full mx-auto mb-3 border-2 border-yellow-400" />
          <h2 className="text-white text-xl font-bold">Register</h2>
          <p className="text-gray-500 text-sm">Join the MARSUPILAMI community</p>
        </div>
        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Full Name</label>
              <input type="text" value={form.fullName} onChange={e => setForm({...form, fullName: e.target.value})} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none" placeholder="John Doe" />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Username</label>
              <input type="text" value={form.username} onChange={e => setForm({...form, username: e.target.value})} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none" placeholder="johndoe" />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-gray-400 text-xs mb-1 block">Email Address</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none" placeholder="john@example.com" />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Password</label>
              <div className="relative">
                <input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none" placeholder="Min 6 chars" />
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Confirm Password</label>
              <input type="password" value={form.confirmPassword} onChange={e => setForm({...form, confirmPassword: e.target.value})} className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none" placeholder="Confirm" />
            </div>
          </div>
          <button type="submit" className="w-full py-3 rounded-xl font-bold text-black text-sm uppercase bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all duration-300">CREATE ACCOUNT</button>
        </form>
        <p className="text-center text-gray-500 text-sm mt-4">Already have an account? <button onClick={onSwitchToLogin} className="text-yellow-400 hover:text-yellow-300 font-medium">Login</button></p>
      </div>
    </div>
  );
};

export const ForgotPasswordModal = ({ isOpen, onClose, onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email'); return; }

    setLoading(true);
    try {
      await forgotPassword({ email });
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
        <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-2xl w-full max-w-md p-6 relative" onClick={e => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X size={20} /></button>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500/10 border-2 border-green-500 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Mail size={32} className="text-green-500" />
            </div>
            <h2 className="text-white text-xl font-bold mb-2">Check Your Email</h2>
            <p className="text-gray-400 text-sm mb-6">
              If an account exists with <span className="text-yellow-400">{email}</span>, you will receive a password reset link shortly.
            </p>
            <p className="text-yellow-400 text-xs mb-6">
              ⚠️ Note: Email sending is currently simulated. Check the backend console logs for the reset link.
            </p>
            <button onClick={onBackToLogin} className="w-full py-3 rounded-xl font-bold text-black text-sm uppercase bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all duration-300">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-[#0d1a0d] border border-[#1a3a1a] rounded-2xl w-full max-w-md p-6 relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white"><X size={20} /></button>
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-yellow-400/10 border-2 border-yellow-400 rounded-full mx-auto mb-3 flex items-center justify-center">
            <Mail size={32} className="text-yellow-400" />
          </div>
          <h2 className="text-white text-xl font-bold">Forgot Password?</h2>
          <p className="text-gray-500 text-sm">Enter your email to receive a reset link</p>
        </div>
        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-sm">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="text-gray-400 text-xs mb-1 block">Email Address</label>
            <input 
              type="email" 
              value={email} 
              onChange={e => setEmail(e.target.value)} 
              className="w-full bg-[#060d06] border border-[#1a3a1a] rounded-lg px-3 py-2.5 text-white text-sm focus:border-yellow-400/50 focus:outline-none" 
              placeholder="Enter your email"
              disabled={loading}
            />
          </div>
          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-3 rounded-xl font-bold text-black text-sm uppercase bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 transition-all duration-300 disabled:opacity-50"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>
        <p className="text-center text-gray-500 text-sm mt-4">
          <button onClick={onBackToLogin} className="text-yellow-400 hover:text-yellow-300 font-medium">Back to Login</button>
        </p>
      </div>
    </div>
  );
};
